import i18n from "@/i18n";
import { detailOr } from "@/lib/apiErrorMessage";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { toast } from "sonner";
import { ExternalLink, ShieldCheck, LogOut } from "lucide-react";

/**
 * LegalAccept — the pre-onboarding legal gate.
 *
 * ARCHITECTURAL RULE (locked by the product owner on 2026-02-27):
 * The SaaS never renders or stores the body of any legal document.
 * The MiseOne public website is the single source of truth. This page
 * therefore only:
 *   • lists each currently-active legal document with its name +
 *     version + Required/Informational badge,
 *   • offers a "Read Document" button that opens the OFFICIAL public
 *     URL in a new browser tab,
 *   • collects the required acceptance checkboxes,
 *   • posts the acceptance record (company + user + role + document +
 *     version + timestamp) to the backend.
 *
 * URLs are supplied by GET /api/legal/status → documents. No legal URL
 * or wording is hardcoded here — new documents (e.g. Cookie Policy,
 * Terms of Service once approved) appear automatically the moment
 * Super Admin activates them.
 */
const REQUIRED_WORDING = {
    get msa() { return i18n.t("ui.LegalAccept.REQUIRED_WORDING.msa", "I have read and accept the Master Service Agreement on behalf of the Customer."); },
    get tos() { return i18n.t("ui.LegalAccept.REQUIRED_WORDING.tos", "I have read and accept the Terms of Service."); },
    get privacy() { return i18n.t("ui.LegalAccept.REQUIRED_WORDING.privacy", "I acknowledge that I have read the Privacy Policy."); },
    get dpa() { return i18n.t("ui.LegalAccept.REQUIRED_WORDING.dpa", "I have read and accept the Data Processing Agreement on behalf of the Customer."); },
    get cookie() { return i18n.t("ui.LegalAccept.REQUIRED_WORDING.cookie", "I acknowledge that I have read the Cookie Policy."); },
};

const DOC_LABELS = {
    get msa() { return i18n.t("ui.LegalAccept.DOC_LABELS.msa", "Master Service Agreement"); },
    get tos() { return i18n.t("ui.LegalAccept.DOC_LABELS.tos", "Terms of Service"); },
    get privacy() { return i18n.t("ui.LegalAccept.DOC_LABELS.privacy", "Privacy Policy"); },
    get dpa() { return i18n.t("ui.LegalAccept.DOC_LABELS.dpa", "Data Processing Agreement"); },
    get sla() { return i18n.t("ui.LegalAccept.DOC_LABELS.sla", "Service Level Agreement"); },
    get cookie() { return i18n.t("ui.LegalAccept.DOC_LABELS.cookie", "Cookie Policy"); },
};

function labelFor(type) {
    return DOC_LABELS[type] || type.toUpperCase();
}

function ReadDocumentButton({ url, type }) {
    return (
        <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`legal-read-${type}`}
            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-neutral-300 bg-white text-xs font-semibold text-neutral-800 hover:bg-neutral-50"
        >
            Read Document <ExternalLink className="h-3.5 w-3.5" />
        </a>
    );
}

export default function LegalAccept() {
    const { setUser } = useApp();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [documents, setDocuments] = useState([]);
    const [pendingRequired, setPendingRequired] = useState([]);
    const [checked, setChecked] = useState({});
    const [error, setError] = useState("");

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get("/legal/status");
                if (cancelled) return;
                setDocuments(data.documents || []);
                setPendingRequired(data.pending_required || []);
                if ((data.pending_required || []).length === 0) {
                    navigate("/", { replace: true });
                    return;
                }
                if (!data.eligible) {
                    navigate("/", { replace: true });
                    return;
                }
            } catch (e) {
                if (!cancelled) setError(detailOr(e?.response?.data?.detail, i18n.t("ui.legalAccept.couldNotLoadLegalDocuments", "Could not load legal documents.")));
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [navigate]);

    const allChecked = pendingRequired.length > 0
        && pendingRequired.every((d) => checked[d.document_type]);

    const acceptAndContinue = async () => {
        if (!allChecked || submitting) return;
        setSubmitting(true);
        setError("");
        try {
            await api.post("/legal/accept", {
                acceptances: pendingRequired.map((d) => ({
                    document_id: d.id,
                    document_type: d.document_type,
                    document_version: d.version,
                })),
            });
            toast.success(i18n.t("ui.legalAccept.legalDocumentsAccepted", "Legal documents accepted"));
            navigate("/", { replace: true });
        } catch (e) {
            setError(detailOr(e?.response?.data?.detail, i18n.t("ui.legalAccept.couldNotRecordAcceptancePleaseRetry", "Could not record acceptance. Please retry.")));
        } finally {
            setSubmitting(false);
        }
    };

    const decline = async () => {
        if (submitting) return;
        setSubmitting(true);
        try {
            await api.post("/legal/decline");
        } catch (_e) { /* ignore — signing out anyway */ }
        setUser?.(null);
        toast.message(i18n.t("ui.legalAccept.youHaveBeenSignedOutPlease", "You have been signed out. Please contact your MiseOne account manager if needed."));
        navigate("/login", { replace: true });
    };

    if (loading) {
        return (
            <div className="min-h-screen grid place-items-center bg-neutral-50 text-neutral-500 text-sm"
                 data-testid="legal-accept-loading">
                {i18n.t("ui.legalAccept.loadingLegalDocuments", "Loading legal documents…")}
            </div>
        );
    }

    // Present every active document, in the canonical order: required
    // documents first (in the order the backend returned them, which
    // matches the ONBOARDING flow) followed by informational ones.
    const requiredDocs = (documents || []).filter((d) => d.is_required);
    const informationalDocs = (documents || []).filter((d) => !d.is_required);
    const orderedDocs = [...requiredDocs, ...informationalDocs];

    return (
        <div className="min-h-screen bg-neutral-50 px-4 py-12" data-testid="legal-accept-page">
            <div className="mx-auto max-w-2xl bg-white border border-neutral-200 rounded-2xl shadow-sm p-8 space-y-6">
                <div className="flex items-center gap-3">
                    <div className="h-11 w-11 rounded-full bg-neutral-900 grid place-items-center">
                        <ShieldCheck className="h-5 w-5 text-white" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-display font-bold">{i18n.t("ui.legalAccept.welcomeToMiseone", "Welcome to MiseOne")}</h1>
                        <p className="text-sm text-neutral-500">
                            {i18n.t("ui.legalAccept.beforeContinuingPleaseReviewTheFollowing", "Before continuing, please review the following legal documents.")}
                        </p>
                    </div>
                </div>

                {error && (
                    <div data-testid="legal-accept-error"
                         className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800">
                        {error}
                    </div>
                )}

                {/* Document list — each with name / version / badge / Read Document.
                    The full document body lives on the public website; the SaaS
                    only shows the button that opens it in a new tab. */}
                <ul className="space-y-3" data-testid="legal-documents-list">
                    {orderedDocs.map((doc) => (
                        <li
                            key={doc.id}
                            data-testid={`legal-doc-${doc.document_type}`}
                            className="rounded-md border border-neutral-200 bg-neutral-50 p-4 flex items-start justify-between gap-4"
                        >
                            <div className="min-w-0">
                                <div className="font-semibold text-sm text-neutral-900">
                                    {labelFor(doc.document_type)}
                                </div>
                                <div className="text-xs text-neutral-500 mt-0.5 flex items-center gap-2">
                                    <span className="font-mono">Version {doc.version}</span>
                                    <span
                                        data-testid={`legal-badge-${doc.document_type}`}
                                        className={`inline-block px-1.5 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide ${
                                            doc.is_required
                                                ? "bg-neutral-900 text-white"
                                                : "bg-neutral-200 text-neutral-700"
                                        }`}
                                    >
                                        {doc.is_required ? i18n.t("ui.legalAccept.required", "Required") : i18n.t("ui.legalAccept.informational", "Informational")}
                                    </span>
                                </div>
                            </div>
                            <ReadDocumentButton url={doc.url} type={doc.document_type} />
                        </li>
                    ))}
                </ul>

                {/* Required acceptance checkboxes — one per required doc, using
                    the exact wording locked by the product owner. */}
                <div className="space-y-3 pt-2 border-t border-neutral-200" data-testid="legal-checkboxes">
                    {pendingRequired.map((doc) => {
                        const wording = REQUIRED_WORDING[doc.document_type]
                            || `I have read and accept the ${labelFor(doc.document_type)}.`;
                        return (
                            <label
                                key={doc.id}
                                className="flex items-start gap-2 cursor-pointer"
                                data-testid={`legal-checkbox-row-${doc.document_type}`}
                            >
                                <input
                                    type="checkbox"
                                    data-testid={`legal-check-${doc.document_type}`}
                                    checked={!!checked[doc.document_type]}
                                    onChange={(e) => setChecked((c) => ({
                                        ...c, [doc.document_type]: e.target.checked,
                                    }))}
                                    className="mt-0.5 h-4 w-4 accent-neutral-900"
                                />
                                <span className="text-sm text-neutral-800">{wording}</span>
                            </label>
                        );
                    })}
                </div>

                <div className="flex flex-wrap items-center justify-between gap-2 pt-2">
                    <button
                        data-testid="legal-decline-btn"
                        onClick={decline}
                        disabled={submitting}
                        className="inline-flex items-center gap-1 h-10 px-4 rounded-md border border-neutral-300 bg-white text-sm font-semibold text-neutral-700 hover:bg-neutral-50 disabled:opacity-60"
                    >
                        <LogOut className="h-4 w-4" /> {i18n.t("ui.legalAccept.declineAmpSignOut", "Decline &amp; sign out")}
                    </button>
                    <button
                        data-testid="legal-accept-btn"
                        onClick={acceptAndContinue}
                        disabled={!allChecked || submitting}
                        className="h-10 px-5 rounded-md bg-neutral-900 text-white text-sm font-semibold hover:bg-neutral-800 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                        {submitting ? i18n.t("ui.legalAccept.recording", "Recording…") : i18n.t("ui.legalAccept.acceptAndContinue", "Accept and Continue")}
                    </button>
                </div>
            </div>
        </div>
    );
}
